import { type ISegment, Session } from '@feathr/blackbox';

export interface ISummaryBlock {
  type: 'text' | 'empty_line' | 'list_item' | 'heading';
  content: string;
  level?: number;
}

export interface ISegmentAIResponse {
  summary: ISummaryBlock[];
  success: boolean;
  segmentJson?: Partial<ISegment>;
  error?: string;
}

export interface ISegmentAIRequest {
  userPrompt: string;
}

/**
 * Parse the Lambda JSON response
 *
 * Success case: {"success": true, "segment_json": {...}, "summary": [...]}
 * Model failure: {"success": false, "summary": [...], "error": "optional error message"}
 *
 * This function distinguishes between:
 * - Model failures (success: false) - shows failure UI with summary
 * - True errors (network, parsing, etc.) - shows generic error message
 */
function parseLambdaResponse(responseData: any): ISegmentAIResponse {
  try {
    if (!responseData.hasOwnProperty('success')) {
      throw new Error('Response missing success field');
    }

    if (!responseData.hasOwnProperty('summary')) {
      throw new Error('Response missing summary field');
    }

    // Handle both old string format and new structured format for backwards compatibility
    let summary: ISummaryBlock[];
    if (typeof responseData.summary === 'string') {
      // Convert old string format to new structured format
      summary = [
        {
          type: 'text',
          content: responseData.summary,
        },
      ];
    } else if (Array.isArray(responseData.summary)) {
      // Validate the structured format
      summary = responseData.summary.filter(
        (block: any) =>
          block &&
          typeof block === 'object' &&
          typeof block.type === 'string' &&
          typeof block.content === 'string',
      );

      if (summary.length === 0) {
        throw new Error('Invalid summary format: no valid summary blocks found');
      }
    } else {
      throw new Error('Invalid summary format: expected array or string');
    }

    const response: ISegmentAIResponse = {
      summary,
      success: responseData.success,
    };

    if (responseData.success) {
      if (!responseData.segment_json) {
        // Treat missing segment_json as a model failure, not a hard error
        return {
          summary,
          success: false,
          error: 'Model returned success but failed to provide segment configuration',
        };
      }

      // segment_json should already be an object, no string parsing needed
      response.segmentJson = responseData.segment_json;
    } else {
      // Model explicitly returned failure - this is expected and should show the failure UI with summary
      // Don't set this as an error - this is a legitimate model response that should be displayed
      response.error = responseData.error;
    }

    return response;
  } catch (error) {
    // This catch block handles true errors (JSON parsing, missing required fields, etc.)
    // Model failures (success: false) are handled above and don't reach this block
    return {
      summary: [
        {
          type: 'text',
          content: 'Error processing response',
        },
      ],
      success: false,
      error: error instanceof Error ? error.message : 'Unknown parsing error',
    };
  }
}

/**
 * Send a natural language prompt to the AWS Lambda processor and return parsed results
 */
export async function generateSegmentFromPrompt(prompt: string): Promise<ISegmentAIResponse> {
  try {
    // Create the request payload with just the user's prompt
    const requestPayload: ISegmentAIRequest = {
      userPrompt: prompt.trim(),
    };

    // Get authentication headers from Session
    const headers = Session.getHeaders();

    const lambdaInvokeUrl = 'https://ss4bcsvytcsydyeik7dm7sgtwu0wqzmn.lambda-url.us-east-1.on.aws/';

    // Post to AWS Lambda URL
    const response = await fetch(lambdaInvokeUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      throw new Error(`AI Lambda request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    return parseLambdaResponse(data);
  } catch (error) {
    return {
      summary: [
        {
          type: 'text',
          content: 'Failed to process request',
        },
      ],
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
